import { config } from '@iheartjane/config';
import { geocode, googleMapsLoader } from '@iheartjane/geolocation';
import { DeliveryAddressFields } from '@iheartjane/menu';
import type { CountryOptions } from '@iheartjane/menu';
import {
  Accordion,
  Box,
  ClockIcon,
  Flex,
  SelectField,
  TextField,
  Typography,
} from '@iheartjane/reefer';
import { sentenceCase } from '@iheartjane/utils';
import { useCallback, useEffect, useState } from 'react';

import type { CheckoutPreferences } from '@jane/shared-ecomm/data-access';

import styles from './MenuQuickCheckoutDrawer.module.css';

export interface DeliveryAddress {
  city: string;
  country_code: string;
  lat: number;
  lng: number;
  state_code: string;
  street: string;
  street2: string;
  zipcode: string;
}
interface QuickCheckoutFulfillmentProps {
  currentReservationMode: string;
  deliveryAddress: DeliveryAddress;
  deliveryMessageEnabled?: boolean;
  fulfillmentTime: string | undefined;
  pickupMessageEnabled?: boolean;
  prefillAddress: CheckoutPreferences['address'];
  reservationMessage: {
    delivery: string | undefined;
    pickup: string | undefined;
  };
  reservationSlotsData:
    { display_name: string; from: number; to: number }[] | undefined;
  reservationSlotsLoading: boolean;
  setDeliveryAddress: React.Dispatch<React.SetStateAction<DeliveryAddress>>;
  setFulfillmentTime: (time: string) => void;
  setIsAddressGeocoded: (isGeocoded: boolean) => void;
  setReservationMessage: React.Dispatch<
    React.SetStateAction<{
      delivery: string | undefined;
      pickup: string | undefined;
    }>
  >;
  storeCountryCode: string | null | undefined;
}

export const QuickCheckoutFulfillment = ({
  currentReservationMode,
  deliveryAddress,
  deliveryMessageEnabled,
  fulfillmentTime,
  pickupMessageEnabled,
  prefillAddress,
  reservationMessage,
  reservationSlotsData,
  reservationSlotsLoading,
  setDeliveryAddress,
  setFulfillmentTime,
  setIsAddressGeocoded,
  setReservationMessage,
  storeCountryCode,
}: QuickCheckoutFulfillmentProps) => {
  const isDelivery = currentReservationMode === 'delivery';

  const [isExpanded, setIsExpanded] = useState(false);
  const [addressError, setAddressError] = useState('');

  const handleAddressSelection = useCallback(
    async (selectedAddress: string) => {
      setAddressError('');
      try {
        const location = await geocode({
          loader: () => googleMapsLoader(config.googlePlacesKey),
          searchCriteria: selectedAddress,
        });

        if (!location.street || !location.city || !location.zipcode) {
          setAddressError('Please enter a complete address.');
          setIsAddressGeocoded(false);
          return;
        }

        setDeliveryAddress((prev) => ({
          ...prev,
          city: location.city || '',
          country_code: location.countryCode || '',
          lat: location.coordinates.lat,
          lng: location.coordinates.long,
          state_code: location.state || '',
          street: location.street || '',
          street2: location.street2 || '',
          zipcode: location.zipcode || '',
        }));
        setIsAddressGeocoded(true);
      } catch {
        setAddressError('Unable to verify address. Please try again.');
        setIsAddressGeocoded(false);
      }
    },
    [setAddressError, setDeliveryAddress, setIsAddressGeocoded]
  );

  const handleAddressChange = useCallback(() => {
    setIsAddressGeocoded(false);
    setAddressError('');
  }, []);

  // TO DO: use compliance language
  const getFulfillmentWording = () => {
    if (currentReservationMode === 'delivery') return 'Delivery details';
    if (currentReservationMode === 'pickup') return 'In-store pickup time';
    if (currentReservationMode === 'curbside') return 'Curbside pickup time';
    return 'Reservation time';
  };

  const slotOptions =
    reservationSlotsData?.map((slot) => ({
      label: slot.display_name,
      value: `${slot.from}>${slot.to}`,
    })) || [];

  useEffect(() => {
    if (reservationSlotsData) {
      const slot = reservationSlotsData[0];

      setFulfillmentTime(`${slot.from}>${slot.to}`);
    }
  }, [reservationSlotsData]);

  return (
    <Box className={styles['checkoutAccordionItem']}>
      <Accordion.Item id="reservation-details">
        <Accordion.Heading onClick={() => setIsExpanded((prev) => !prev)}>
          <Flex gap={16}>
            <div>
              <ClockIcon />
            </div>
            <Flex flexDirection="column">
              <Typography variant="body-semibold">
                {getFulfillmentWording()}
              </Typography>
              <Accordion.Summary>
                {isDelivery && !!deliveryAddress.street && (
                  <Typography>
                    {`${deliveryAddress.street}${deliveryAddress.street2 ? ` ${deliveryAddress.street2}` : ''}, ${deliveryAddress.city}, ${deliveryAddress.state_code} ${deliveryAddress.zipcode}`}
                  </Typography>
                )}
                <Typography>
                  {
                    reservationSlotsData?.find(
                      (slot) =>
                        slot.from === Number(fulfillmentTime?.split('>')[0])
                    )?.display_name
                  }
                </Typography>
              </Accordion.Summary>
            </Flex>
            <Flex ml="auto" alignItems="center" height="100%">
              <Accordion.Indicator iconSize="md" />
            </Flex>
          </Flex>
        </Accordion.Heading>
        <Accordion.Content
          className={
            isExpanded ? styles['fulfillmentAccordionContent'] : undefined
          }
        >
          <Flex
            className={styles['fulfillmentField']}
            flexDirection="column"
            mt={16}
            gap={16}
          >
            {isDelivery && (
              <DeliveryAddressFields
                addressErrorMessage={addressError}
                addressLabel="Delivery address"
                countries={
                  storeCountryCode
                    ? [storeCountryCode as CountryOptions]
                    : undefined
                }
                defaultAddress={prefillAddress.street || ''}
                defaultStreet2={prefillAddress.street2 || ''}
                disableMobileInputStyling
                googlePlacesKey={config.googlePlacesKey}
                onAddressChange={handleAddressChange}
                onAddressSelection={handleAddressSelection}
                onStreet2Blur={(value) =>
                  setDeliveryAddress((prev) => ({
                    ...prev,
                    street2: value,
                  }))
                }
              />
            )}
            <SelectField
              borderRadius="xs"
              disableMobileInputStyling
              label={`${sentenceCase(currentReservationMode || 'Reservation')} time`}
              name="fulfillmentTime"
              loading={reservationSlotsLoading}
              options={slotOptions}
              value={fulfillmentTime}
              onChange={(slot) => {
                setFulfillmentTime(slot);
              }}
            />

            {isDelivery && deliveryMessageEnabled && (
              <TextField
                disableMobileInputStyling
                borderRadius="xs"
                label="Delivery instructions"
                name="deliveryMessage"
                placeholder="e.g. Gate code"
                defaultValue={reservationMessage.delivery}
                maxLength={300}
                onBlur={(message) =>
                  setReservationMessage((prev) => ({
                    ...prev,
                    delivery: message,
                  }))
                }
              />
            )}
            {!isDelivery && pickupMessageEnabled && (
              <TextField
                disableMobileInputStyling
                borderRadius="xs"
                label="Pickup notes"
                name="pickupMessage"
                defaultValue={reservationMessage.pickup}
                maxLength={300}
                onBlur={(message) =>
                  setReservationMessage((prev) => ({
                    ...prev,
                    pickup: message,
                  }))
                }
              />
            )}
          </Flex>
        </Accordion.Content>
      </Accordion.Item>
    </Box>
  );
};
